import {
  Button,
  Dialog,
  DialogContent,
  InputBase,
  FormLabel,
  TextField,
  Typography,
  Divider,
  Box,
  IconButton,
  InputAdornment,
  OutlinedInput,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { UPDATE_TYPE_WITH_FIELDS_DIALOG, CREATE_PRODUCT_ATTRIBUTE_DIALOG, UPDATE_PRODUCT_ATTRIBUTE_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { FIELD_VALUE_TYPE_LABEL, FIELD_VALUE_TYPE } from '../../../../../core/constants/fieldValueType';
import { PutTypePayloadRequest } from '../../../../../core/modules/master/interfaces/put-type';
import Swal from 'sweetalert2';
import { TypeLabel } from '../categories/CreateDialog';
import { ERROR } from '../../../../../core/constants/errorCode';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface Props {
  type: tType
}

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
  description: yup.string().required('Campo requerido'),
  extra_fields: yup.array().of(yup.object().shape({
    name: yup.string().required('Campo requerido'),
    type: yup.string().required('Campo requerido'),
    in_description: yup.boolean().required('Campo requerido'),
    ref: yup.string().optional(),
  })).required('Campo requerido'),
});

const UpdateDialogWithFields = (props: Props) => {
  const { type } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { type: selectedType, errorCode, putType, deleteType, getTypes, setType } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('');
  const [valueType, setValueType] = useState('');
  const [inDescription, setInDescription] = useState(false);
  const [editing, setEditting] = useState(-1);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('');
  const [newInDescription, setNewInDescription] = useState(false);
  const [fieldNameError, setFieldNameError] = useState('');

  const { control, formState, handleSubmit, setValue, resetField } = useForm({
    mode: 'onChange',
    defaultValues: {
      extra_fields: [],
    },
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;
  const { fields, append, remove, update, move } = useFieldArray({
    control,
    name: 'extra_fields',
  });

  const resetForm = useCallback(() => {
    resetField('description');
    resetField('extra_fields');
    resetField('name');
    setType(null);
    // Limpiar campos de entrada para nuevos campos
    setFieldName('');
    setFieldType('');
    setValueType('');
    setInDescription(false);
    setFieldNameError('');
    setEditting(-1);
    closeDialog(UPDATE_TYPE_WITH_FIELDS_DIALOG);
  }, [closeDialog, resetField, setType]);

  useEffect(() => {
    if (selectedType) {
      setValue("name", selectedType.name);
      setValue("description", selectedType.description);
      setValue("extra_fields", selectedType.extra_fields || []);
      // Limpiar campos de entrada para nuevos campos cuando se carga un nuevo tipo
      setFieldName('');
      setFieldType('');
      setValueType('');
      setInDescription(false);
      setFieldNameError('');
      setEditting(-1);
    }
  }, [selectedType, setValue]);

  const onSubmit = useCallback(
    (values: PutTypePayloadRequest) => {
      if (companyId && selectedType) {
        setLoading(true);
        // Tomar los campos de fields (useFieldArray), agregar posición y enviar como extra_fields
        const extra_fields = Array.isArray(fields)
          ? fields.map((field, idx) => ({ ...field, position: idx }))
          : [];
        putType(companyId, selectedType.id, { ...values, extra_fields }, type, () => {
          resetForm();
          getTypes(companyId, type);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, selectedType, putType, type, resetForm, getTypes, fields]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar el tipo ${selectedType?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && selectedType) {
        setLoading(true);

        deleteType(
          companyId,
          selectedType.id,
          type,
          () => {
            setLoading(false);
            getTypes(companyId, type);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [companyId, type, deleteType, getTypes, resetForm, selectedType]);


  const addField = useCallback(() => {
    if (fieldName && valueType) {
      // Obtener los campos actuales directamente del formulario
      const currentFields = Array.isArray(fields) ? fields : [];
      const trimmedFieldName = fieldName.trim();
      
      // Verificar duplicados de manera más estricta
      const existingField = currentFields.find(field => 
        field.name.trim().toLowerCase() === trimmedFieldName.toLowerCase()
      );
      
      if (existingField) {
        setFieldNameError('Ya existe un campo con este nombre');
        return;
      }
      
      // Solo agregar si no hay duplicados
      append({
        name: trimmedFieldName.toUpperCase(),
        type: valueType,
        in_description: inDescription,
        ref: fieldType === 'custom' ? '' : fieldType
      });
      
      setFieldName('');
      setFieldType('');
      setInDescription(false);
      setFieldNameError('');
    }
  }, [fieldName, valueType, inDescription, append, fieldType, fields]);

  const editField = useCallback((index: number) => {
    setEditting(index);
    setNewFieldName(fields[index].name);
    setNewFieldType(fields[index].type);
    setNewInDescription(fields[index].in_description);
    setFieldNameError('');
  }, [fields]);

  const cancelEditting = useCallback(() => {
    setEditting(-1);
    setFieldNameError('');
  }, []);



  const saveEditting = useCallback((index: number) => {
    // Usar el array actualizado de campos
    const currentFields = Array.isArray(fields) ? fields : [];
    // Verificar si ya existe otro campo con el mismo nombre (excluyendo el campo actual)
    const existingField = currentFields.find((field, fieldIndex) => 
      fieldIndex !== index && field.name.trim().toLowerCase() === newFieldName.trim().toLowerCase()
    );
    if (existingField) {
      setFieldNameError('Ya existe un campo con este nombre');
      return;
    }
    
    update(index, { 
      ...currentFields[index], 
      name: newFieldName.toUpperCase(),
      type: newFieldType,
      in_description: newInDescription
    });
    setEditting(-1);
    setFieldNameError('');
  }, [fields, newFieldName, newFieldType, newInDescription, update]);

  const selectFieldType = useCallback((type: string) => {
    if (type === 'format') {
      setFieldName('Formato');
      setValueType('numeric');
    } else if (type === 'genre') {
      setFieldName('Género');
      setValueType('numeric');
    } else {
      setFieldName('');
      setValueType('');
    }
    setFieldType(type);
  }, []);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[600px] max-w-[800px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_TYPE_WITH_FIELDS_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Tipo de {TypeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <FormLabel>Campos</FormLabel>
                    <Box
                      component="form"
                      className="border rounded border-gray-400 hover:border-gray-600 mb-20"
                      sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }}
                    >
                      <div className="flex flex-col">
                        <Select
                          displayEmpty
                          value={fieldType}
                          className="removeSelectBourder"
                          classes={{ filled: 'border-0' }}
                          onChange={(ev) => selectFieldType(ev.target.value)}
                          size='small'
                          input={<OutlinedInput />}
                          renderValue={(selected) => {
                            if (selected === 'custom') {
                              return 'Personalizado';
                            } else if (selected === 'format') {
                              return 'Formato';
                            } else if (selected === 'genre') {
                              return 'Género';
                            } else {
                              return <span className='text-gray-500'>Tipo de campo</span>;
                            }
                          }}
                          MenuProps={MenuProps}
                          inputProps={{ 'aria-label': 'Without label' }}
                        >
                          <MenuItem value='custom'>Personalizado</MenuItem>
                          <MenuItem value='format'>Formato</MenuItem>
                          <MenuItem value='genre'>Género</MenuItem>
                        </Select>
                        <Divider sx={{ m: 0.5 }} orientation="horizontal" />
                        <div className="flex items-center">
                          <InputBase
                            sx={{ ml: 1, flex: 1 }}
                            placeholder="Nombre"
                            value={fieldName}
                            disabled={loading || fieldType !== 'custom'}
                            onChange={(ev) => {
                              const newValue = ev.target.value.toUpperCase().trim();
                              setFieldName(newValue);
                              
                              // Validación en tiempo real
                              if (newValue.trim()) {
                                const currentFields = Array.isArray(fields) ? fields : [];
                                const existingField = currentFields.find(field => 
                                  field.name.trim().toLowerCase() === newValue.trim().toLowerCase()
                                );
                                if (existingField) {
                                  setFieldNameError('Ya existe un campo con este nombre');
                                } else {
                                  setFieldNameError('');
                                }
                              } else {
                                setFieldNameError('');
                              }
                            }}
                          />
                          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                          <Select
                            displayEmpty
                            value={valueType}
                            className="removeSelectBourder"
                            classes={{ filled: 'border-0' }}
                            onChange={(ev) => setValueType(ev.target.value)}
                            size='small'
                            input={<OutlinedInput />}
                            renderValue={(selected) => {
                              if (selected === 'alphanumeric') {
                                return 'Texto';
                              } else if (selected === 'numeric') {
                                return 'Numérico';
                              } else if (selected === 'boolean') {
                                return 'Booleano';
                              } else {
                                return <span className='text-gray-500'>Tipo de valor</span>;
                              }
                            }}
                            MenuProps={MenuProps}
                            disabled={loading || fieldType !== 'custom'}
                            inputProps={{ 'aria-label': 'Without label' }}
                          >
                            <MenuItem value={FIELD_VALUE_TYPE.ALPHANUMERIC}>
                              {FIELD_VALUE_TYPE_LABEL[FIELD_VALUE_TYPE.ALPHANUMERIC]}
                            </MenuItem>
                            <MenuItem value={FIELD_VALUE_TYPE.NUMBERIC}>
                              {FIELD_VALUE_TYPE_LABEL[FIELD_VALUE_TYPE.NUMBERIC]}
                            </MenuItem>
                            <MenuItem value={FIELD_VALUE_TYPE.BOOLEAN}>
                              {FIELD_VALUE_TYPE_LABEL[FIELD_VALUE_TYPE.BOOLEAN]}
                            </MenuItem>
                          </Select>
                        </div>
                        {fieldNameError && (
                          <Typography color="error" variant="caption" className="ml-8 mt-4">
                            {fieldNameError}
                          </Typography>
                        )}
                        <Divider sx={{ m: 0.5 }} orientation="horizontal" />
                        <div className="flex items-center ml-8">
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled={loading || fieldType !== 'custom'}
                                onChange={(ev) => {
                                  setInDescription(ev.target.checked);
                                }}
                                checked={inDescription}
                              />
                            }
                            label="Agregar a descripción"
                          />
                        </div>
                      </div>
                      <Divider sx={{ height: 100, m: 0.5 }} orientation="vertical" />
                      <IconButton
                        color="primary"
                        sx={{ p: '10px' }}
                        disabled={loading}
                        onClick={addField}
                        type="button"
                      >
                        <AddOutlinedIcon />
                      </IconButton>
                    </Box>
                    <div 
                      className="max-h-[500px] overflow-y-auto border rounded p-4" 
                      style={{ 
                        scrollbarWidth: 'thin', 
                        scrollbarColor: '#cbd5e0 #f1f5f9',
                        scrollbarGutter: 'stable'
                      }}
                    >
                      <Typography variant="subtitle2" className="mb-4 text-gray-600">
                        Campos personalizados ({fields.length})
                      </Typography>
                      <DragDropContext onDragEnd={(result: any) => {
                        if (!result.destination) return;
                        if (result.source.index !== result.destination.index) {
                          // Reordenar usando useFieldArray.move
                          move(result.source.index, result.destination.index);
                        }
                      }}>
                        <Droppable droppableId="campos-personalizados">
                          {(provided: any) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {fields.length === 0 ? (
                                <Typography variant="body2" className="text-gray-500 text-center py-8">
                                  No hay campos personalizados agregados
                                </Typography>
                              ) : (
                                fields.map((_, index) => (
                                  <Draggable key={fields[index].id} draggableId={String(fields[index].id)} index={index}>
                                    {(provided: any) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className="mb-2 flex items-center"
                                      >
                                        {/* Handle de arrastre */}
                                        <span {...provided.dragHandleProps} style={{ cursor: 'grab', marginRight: 12, display: 'flex', alignItems: 'center' }}>
                                          <svg width="16" height="16" style={{ opacity: 0.5 }}><circle cx="4" cy="4" r="1.5"/><circle cx="4" cy="12" r="1.5"/><circle cx="12" cy="4" r="1.5"/><circle cx="12" cy="12" r="1.5"/></svg>
                                        </span>
                                        {/* Campo personalizado original */}
                                        <Controller
                                          control={control}
                                          name={`extra_fields.${index}.name`}
                                          render={({ field }) => (
                                            <div className='border flex justify-between py-4 pl-8 rounded flex-1'>
                                              <div className='max-w-[300px] overflow-hidden text-ellipsis'>
                                                                                                 <div>
                                                   <Typography>{fields[index].name}</Typography>
                                                 </div>
                                                                                                 {editing === index ? (
                                                   <div className="mt-4 space-y-3">
                                                     <div className="flex items-center gap-2">
                                                       <Typography variant="caption" className="text-gray-500 min-w-[60px]">
                                                         Nombre:
                                                       </Typography>
                                                       <InputBase
                                                         value={newFieldName}
                                                         size="small"
                                                         onChange={(ev) => setNewFieldName(ev.target.value)}
                                                         className="border rounded px-2 py-1 flex-1"
                                                       />
                                                     </div>
                                                     <div className="flex items-center gap-2">
                                                       <Typography variant="caption" className="text-gray-500 min-w-[60px]">
                                                         Tipo:
                                                       </Typography>
                                                       <Select
                                                         value={newFieldType}
                                                         onChange={(ev) => setNewFieldType(ev.target.value)}
                                                         size="small"
                                                         className="min-w-[120px]"
                                                       >
                                                         <MenuItem value={FIELD_VALUE_TYPE.ALPHANUMERIC}>
                                                           {FIELD_VALUE_TYPE_LABEL[FIELD_VALUE_TYPE.ALPHANUMERIC]}
                                                         </MenuItem>
                                                         <MenuItem value={FIELD_VALUE_TYPE.NUMBERIC}>
                                                           {FIELD_VALUE_TYPE_LABEL[FIELD_VALUE_TYPE.NUMBERIC]}
                                                         </MenuItem>
                                                         <MenuItem value={FIELD_VALUE_TYPE.BOOLEAN}>
                                                           {FIELD_VALUE_TYPE_LABEL[FIELD_VALUE_TYPE.BOOLEAN]}
                                                         </MenuItem>
                                                       </Select>
                                                     </div>
                                                                                                           <div className="flex items-center gap-2">
                                                        <Typography variant="caption" className="text-gray-500 min-w-[60px]">
                                                          Agregar a Descripción:
                                                        </Typography>
                                                        <Checkbox
                                                          size="small"
                                                          checked={newInDescription}
                                                          onChange={(ev) => setNewInDescription(ev.target.checked)}
                                                        />
                                                      </div>
                                                                                                           <div className="flex gap-2 mt-2">
                                                        <Button
                                                          size="small"
                                                          variant="contained"
                                                          color="success"
                                                          onClick={() => saveEditting(index)}
                                                          startIcon={<DoneIcon />}
                                                        >
                                                          Guardar
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          color="error"
                                                          onClick={cancelEditting}
                                                          startIcon={<CloseIcon />}
                                                        >
                                                          Cancelar
                                                        </Button>
                                                      </div>
                                                   </div>
                                                                                                   ) : (
                                                    <div className='flex gap-4 items-center'>
                                                      <div className="flex items-center gap-2">
                                                        <Typography variant="caption" className="text-gray-500">
                                                          Tipo:
                                                        </Typography>
                                                        <Typography variant="caption" className="font-medium">
                                                          {FIELD_VALUE_TYPE_LABEL[fields[index].type]}
                                                        </Typography>
                                                      </div>
                                                      <div className="flex items-center gap-2">
                                                        <Typography variant="caption" className="text-gray-500">
                                                          En descripción:
                                                        </Typography>
                                                        <Checkbox
                                                          size="small"
                                                          checked={fields[index].in_description}
                                                          disabled
                                                        />
                                                      </div>
                                                    </div>
                                                  )}
                                              </div>
                                              {editing !== index && (
                                                <div>
                                                  {fields[index].ref === '' && (
                                                    <IconButton
                                                      color="secondary"
                                                      disabled={loading}
                                                      onClick={() => editField(index)}
                                                      type="button"
                                                    >
                                                      <EditIcon />
                                                    </IconButton>
                                                  )}
                                                  <IconButton
                                                    color="error"
                                                    disabled={loading}
                                                    onClick={() => {
                                                      Swal.fire({
                                                        title: 'Confirmar eliminación',
                                                        text: `¿Eliminar el campo "${fields[index].name}"?`,
                                                        icon: 'warning',
                                                        showCancelButton: true,
                                                        confirmButtonColor: '#d33',
                                                        cancelButtonColor: '#9e9e9e',
                                                        confirmButtonText: 'Sí, eliminar',
                                                        cancelButtonText: 'Cancelar',
                                                      }).then((result) => {
                                                        if (result.isConfirmed) {
                                                          remove(index);
                                                        }
                                                      });
                                                    }}
                                                    type="button"
                                                  >
                                                    <DeleteIcon />
                                                  </IconButton>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </div>
                  </div>

                  {errorCode === ERROR.ERR_409 && (
                    <Typography color="error" className="text-md text-center mb-24">
                      Ya existe un tipo con ese nombre
                    </Typography>
                  )}

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ borderRadius: 50 }}
                      disabled={loading}
                      type="button"
                      onClick={onDelete}
                    >
                      <Spinner show={loading} />
                      Eliminar
                    </Button>                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading || editing !== -1 || (Array.isArray(dialogs) && (dialogs.includes(CREATE_PRODUCT_ATTRIBUTE_DIALOG) || dialogs.includes(UPDATE_PRODUCT_ATTRIBUTE_DIALOG)))}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialogWithFields;
